/* eslint-disable */
import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { motion, AnimatePresence } from "framer-motion";
import {
  Box,
  TextField,
  Button,
  IconButton,
  Stack,
  MenuItem,
  Typography,
  CircularProgress,
  useTheme,
} from "@mui/material";
import { Close as CloseIcon } from "@mui/icons-material";
import { updateExistingDesignation } from "store/features/company/userManagement/designation/designationActions";
import { fetchDepartments } from "store/features/company/department/departmentActions";
import { selectDepartments } from "store/features/company/department/departmentSelectors";
import editDesignationSvg from "assets/modals/edit.svg";
import { showErrorToast } from "utils/toastNotification";
import useFormValidation from "hooks/useFormValidation";
import { designationValidationRules } from "constants/validationRules";

const EditDesignationModal = ({ isOpen, onClose, onSuccess, designation }) => {
  const theme = useTheme();
  const dispatch = useDispatch();
  const departments = useSelector(selectDepartments);

  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    department_id: "",
    name: "",
    code: "",
    description: "",
    level: "",
    salary_range_min: "",
    salary_range_max: "",
    status: "",
  });

  const {
    errors,
    validateField,
    validateForm,
    resetErrors,
  } = useFormValidation(designationValidationRules);

  useEffect(() => {
    if (isOpen) {
      dispatch(fetchDepartments());
      const originalOverflow = document.body.style.overflow;
      document.body.style.overflow = "hidden";
      return () => {
        document.body.style.overflow = originalOverflow;
      };
    }
  }, [isOpen, dispatch]);

  useEffect(() => {
    if (designation && isOpen) {
      setFormData({
        department_id: designation.department_id || "",
        name: designation.name || "",
        code: designation.code || "",
        description: designation.description || "",
        level: designation.level || "",
        salary_range_min: designation.salary_range_min || "",
        salary_range_max: designation.salary_range_max || "",
        status: designation.status || "active",
      });
      resetErrors();
    }
  }, [designation, isOpen, resetErrors]);

  const handleChange = (e) => {
    const { name, value } = e.target;

    setFormData((prev) => {
      const updatedData = {
        ...prev,
        [name]: value,
      };
      validateField(name, value, updatedData);
      return updatedData;
    });
  };

  const handleLevelChange = (e) => {
    const { name, value } = e.target;
    // Allow only numbers between 1-5
    if (value === "" || /^[1-5]$/.test(value)) {
      setFormData((prev) => {
        const updatedData = {
          ...prev,
          [name]: value,
        };
        validateField(name, value, updatedData);
        return updatedData;
      });
    }
  };

  const handleSalaryChange = (e) => {
    const { name, value } = e.target;
    // Allow empty or positive numbers
    if (value === "" || /^\d*\.?\d*$/.test(value)) {
      setFormData((prev) => {
        const updatedData = {
          ...prev,
          [name]: value,
        };
        validateField(name, value, updatedData);
        return updatedData;
      });
    }
  };

  const handleCodeChange = (e) => {
    const { name, value } = e.target;
    const newValue = value.toUpperCase();

    setFormData((prev) => {
      const updatedData = {
        ...prev,
        [name]: newValue,
      };
      validateField(name, newValue, updatedData);
      return updatedData;
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm(formData)) {
      showErrorToast("Please fix the highlighted fields.");
      return;
    }

    setLoading(true);

    const formattedData = {
      department_id: Number(formData.department_id),
      name: formData.name.trim(),
      code: formData.code.toUpperCase(),
      description: formData.description || "",
      level: Number(formData.level),
      salary_range_min: formData.salary_range_min
        ? Number(formData.salary_range_min)
        : null,
      salary_range_max: formData.salary_range_max
        ? Number(formData.salary_range_max)
        : null,
      status: formData.status || "active",
    };

    const result = await dispatch(
      updateExistingDesignation(designation.id, formattedData),
    );

    if (result.success) {
      onSuccess();
      onClose();
      resetErrors();
    }
    setLoading(false);
  };

  const inputSx = {
    "& .MuiOutlinedInput-root": {
      borderRadius: "12px",
      backgroundColor: theme.palette.mode === "dark" ? "#1e293b" : "#f9fafb",
      "& fieldset": {
        borderColor: theme.palette.mode === "dark" ? "#475569" : "#d1d5db",
      },
      "&:hover fieldset": {
        borderColor: "#f59e0b",
      },
      "&.Mui-focused fieldset": {
        borderColor: "#f59e0b",
        borderWidth: "2px",
      },
    },
    "& .MuiInputLabel-root": {
      color: "text.secondary",
      position: "relative",
      transform: "none",
      marginBottom: "8px",
      display: "block",
      "&.Mui-focused": {
        color: "primary.main",
      },
    },
    "& .MuiInputLabel-asterisk": {
      color: "#ef4444",
    },
  };

  const statusOptions = [
    { value: "active", label: "Active" },
    { value: "inactive", label: "Inactive" },
  ];

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          <style>
            {`
          .MuiPopover-root {
            z-index: 1400 !important;
          }
          .MuiPopper-root {
            z-index: 1400 !important;
          }
        `}
          </style>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
            style={{
              position: "fixed",
              inset: 0,
              zIndex: 1300,
              backgroundColor:
                theme.palette.mode === "dark"
                  ? "rgba(252, 226, 181, 0.14)"
                  : "rgba(0, 0, 0, 0.6)",
              backdropFilter: "blur(4px)",
            }}
          />

          <motion.div
            initial={{ x: "100%", opacity: 0 }}
            animate={{ x: 0, opacity: 1 }}
            exit={{ x: "100%", opacity: 0 }}
            transition={{ type: "spring", damping: 25, stiffness: 200 }}
            style={{
              position: "fixed",
              right: 0,
              top: 0,
              zIndex: 1301,
              height: "100%",
              width: "100%",
              maxWidth: "600px",
              overflowY: "auto",
              backgroundColor:
                theme.palette.mode === "dark" ? "#0f172a" : "#ffffff",
              boxShadow: theme.shadows[24],
            }}
          >
            <Box
              sx={{
                position: "sticky",
                top: 0,
                zIndex: 10,
                borderBottom: `1px solid ${theme.palette.mode === "dark" ? "#1e293b" : "#f3f4f6"
                  }`,
                backgroundColor:
                  theme.palette.mode === "dark" ? "#0f172a" : "#ffffff",
              }}
            >
              <Box sx={{ p: 3, pr: 7 }}>
                <Stack direction="row" spacing={2} alignItems="flex-start">
                  <Box flexShrink={0}>
                    <Box
                      component="img"
                      src={editDesignationSvg}
                      alt="Edit Designation"
                      sx={{
                        width: 80,
                        height: 80,
                        objectFit: "contain",
                        transition: "all 0.3s",
                        "&:hover": {
                          filter: "drop-shadow(0 4px 8px rgba(245,158,11,0.3))",
                        },
                      }}
                      onError={(e) => {
                        e.target.style.display = "none";
                      }}
                    />
                  </Box>
                  <Box flex={1}>
                    <Typography
                      variant="h5"
                      component="h2"
                      sx={{
                        fontWeight: "bold",
                        color: "text.primary",
                      }}
                    >
                      Edit Designation
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{
                        mt: 0.5,
                        color: "text.secondary",
                      }}
                    >
                      Update job title or position details
                    </Typography>
                  </Box>
                </Stack>
              </Box>

              <IconButton
                onClick={onClose}
                sx={{
                  position: "absolute",
                  top: 20,
                  right: 20,
                  width: 36,
                  height: 36,
                  borderRadius: "8px",
                  color: "text.secondary",
                  transition: "all 0.2s ease",
                  "&:hover": {
                    backgroundColor: "#f59e0b",
                    color: "#ffffff",
                    transform: "scale(1.05)",
                  },
                  "&:active": {
                    transform: "scale(0.95)",
                  },
                }}
              >
                <CloseIcon sx={{ fontSize: 20 }} />
              </IconButton>
            </Box>

            <form onSubmit={handleSubmit}>
              <Box sx={{ p: 3 }}>
                <Stack spacing={3}>
                  {/* Department and Name Row */}
                  <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
                    <TextField
                      fullWidth
                      select
                      label="Department"
                      name="department_id"
                      value={formData.department_id}
                      onChange={handleChange}
                      required
                      error={!!errors.department_id}
                      helperText={errors.department_id}
                      sx={inputSx}
                      InputLabelProps={{
                        shrink: true,
                      }}
                    >
                      <MenuItem value="">Select Department</MenuItem>
                      {departments.map((dept) => (
                        <MenuItem key={dept.id} value={dept.id}>
                          {dept.name} ({dept.code})
                        </MenuItem>
                      ))}
                    </TextField>
                    <TextField
                      fullWidth
                      label="Designation Name"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      required
                      placeholder="e.g., Senior Software Engineer"
                      error={!!errors.name}
                      helperText={errors.name || "Display name for the designation"}
                      sx={inputSx}
                      InputLabelProps={{
                        shrink: true,
                      }}
                    />
                  </Stack>

                  {/* Code and Level Row */}
                  <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
                    <TextField
                      fullWidth
                      label="Designation Code"
                      name="code"
                      value={formData.code}
                      onChange={handleCodeChange}
                      required
                      placeholder="e.g., SSE"
                      error={!!errors.code}
                      helperText={errors.code || "Short code for the designation (auto-uppercase)"}
                      sx={inputSx}
                      InputLabelProps={{
                        shrink: true,
                      }}
                    />
                    <TextField
                      fullWidth
                      label="Level"
                      name="level"
                      value={formData.level}
                      onChange={handleLevelChange}
                      required
                      placeholder="1-5"
                      error={!!errors.level}
                      helperText={errors.level || "Position level (1 to 5)"}
                      sx={inputSx}
                      InputLabelProps={{
                        shrink: true,
                      }}
                    />
                  </Stack>

                  {/* Description */}
                  <TextField
                    fullWidth
                    label="Description"
                    name="description"
                    value={formData.description}
                    onChange={handleChange}
                    multiline
                    rows={3}
                    placeholder="Describe the responsibilities and requirements for this position..."
                    helperText={errors.description || "Brief description of the role"}
                    error={!!errors.description}
                    sx={inputSx}
                    InputLabelProps={{
                      shrink: true,
                    }}
                  />

                  {/* Salary Range Row */}
                  <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
                    <TextField
                      fullWidth
                      label="Minimum Salary (₹)"
                      name="salary_range_min"
                      value={formData.salary_range_min}
                      onChange={handleSalaryChange}
                      placeholder="30000"
                      error={!!errors.salary_range_min}
                      helperText={errors.salary_range_min || "Minimum salary for this position"}
                      sx={inputSx}
                      InputLabelProps={{
                        shrink: true,
                      }}
                    />
                    <TextField
                      fullWidth
                      label="Maximum Salary (₹)"
                      name="salary_range_max"
                      value={formData.salary_range_max}
                      onChange={handleSalaryChange}
                      placeholder="70000"
                      error={!!errors.salary_range_max}
                      helperText={errors.salary_range_max || "Maximum salary for this position"}
                      sx={inputSx}
                      InputLabelProps={{
                        shrink: true,
                      }}
                    />
                  </Stack>

                  {/* Status */}
                  <TextField
                    fullWidth
                    select
                    label="Status"
                    name="status"
                    value={formData.status}
                    onChange={handleChange}
                    required
                    sx={inputSx}
                    InputLabelProps={{
                      shrink: true,
                    }}
                  >
                    {statusOptions.map((option) => (
                      <MenuItem key={option.value} value={option.value}>
                        {option.label}
                      </MenuItem>
                    ))}
                  </TextField>
                </Stack>
              </Box>

              <Box
                sx={{
                  p: 3,
                  borderTop: `1px solid ${theme.palette.mode === "dark" ? "#1e293b" : "#f3f4f6"
                    }`,
                  display: "flex",
                  gap: 2,
                }}
              >
                <Button
                  onClick={onClose}
                  disabled={loading}
                  fullWidth
                  sx={{
                    borderRadius: "12px",
                    py: 1.2,
                    textTransform: "none",
                    fontWeight: 500,
                    borderColor:
                      theme.palette.mode === "dark" ? "#475569" : "#d1d5db",
                    color:
                      theme.palette.mode === "dark" ? "#d1d5db" : "#374151",
                    "&:hover": {
                      borderColor: "#f59e0b",
                      backgroundColor:
                        theme.palette.mode === "dark"
                          ? "rgba(245,158,11,0.1)"
                          : "#fffbeb",
                    },
                  }}
                  variant="outlined"
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  disabled={loading}
                  fullWidth
                  sx={{
                    borderRadius: "12px",
                    py: 1.2,
                    textTransform: "none",
                    fontWeight: 500,
                    background:
                      "linear-gradient(135deg, #f59e0b 0%, #d97706 100%)",
                    color: "#ffffff",
                    boxShadow: "0 10px 15px -3px rgba(245, 158, 11, 0.25)",
                    "&:hover": {
                      background:
                        "linear-gradient(135deg, #d97706 0%, #b45309 100%)",
                      transform: "scale(1.02)",
                      boxShadow: "0 20px 25px -5px rgba(245, 158, 11, 0.3)",
                    },
                    "&:disabled": {
                      opacity: 0.7,
                    },
                  }}
                >
                  {loading ? (
                    <CircularProgress size={24} sx={{ color: "#ffffff" }} />
                  ) : (
                    "Update Designation"
                  )}
                </Button>
              </Box>
            </form>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
};

export default EditDesignationModal;